import {
  AttributeGroup,
  attributeGroup,
  booleanAttr,
  lookupBucket,
  marker,
  numberAttr,
  rangeBucket,
  stringAttr,
  thresholdAttr,
  thresholdBucket,
} from "@cartographerio/atlas-map";
import {
  ExtendedRiverflySpeciesEnum,
  RiverflySpeciesEnum,
  UrbanRiverflySpeciesEnum,
} from "@cartographerio/inventory-enums";
import { Enum } from "@cartographerio/util";

const white = marker.fromColor("#f7f7f7");

// [0,360] [0,1] -> color
function speciesColor(hue: number, lightness: number) {
  return marker.gradient(hue, 50, lightness);
}

const siteGroup = attributeGroup({
  label: "Location",
  attributes: [
    stringAttr({
      attributeId: "catchment",
      label: "Catchment",
      buckets: "auto",
    }),
    stringAttr({ attributeId: "river", label: "River", buckets: "auto" }),
    stringAttr({ attributeId: "site", label: "Site", buckets: "auto" }),
    stringAttr({
      attributeId: "moduleId",
      label: "Survey Type",
      buckets: [
        lookupBucket("riverfly", speciesColor(0, 1), "Riverfly"),
        lookupBucket("urbanRiverfly", speciesColor(144, 1), "Urban Riverfly"),
        lookupBucket(
          "extendedRiverfly",
          speciesColor(216, 1),
          "Extended Riverfly"
        ),
      ],
    }),
  ],
});

const triggerGroup = attributeGroup({
  label: "ARMI Pollution Warning",
  attributes: [
    thresholdAttr({
      attributeId: "trigger.score",
      label: "ARMI Score",
      buckets: [
        thresholdBucket(0, white),
        thresholdBucket(4, speciesColor(0, 3 / 3)),
        thresholdBucket(8, speciesColor(0, 2 / 3)),
        thresholdBucket(12, speciesColor(0, 1 / 3)),
        thresholdBucket(16, speciesColor(0, 0 / 3)),
      ],
    }),
    thresholdAttr({
      attributeId: "trigger.level",
      label: "ARMI Trigger Level",
      buckets: [
        thresholdBucket(0, white),
        thresholdBucket(4, speciesColor(120, 3 / 3)),
        thresholdBucket(8, speciesColor(120, 2 / 3)),
        thresholdBucket(12, speciesColor(120, 1 / 3)),
        thresholdBucket(16, speciesColor(120, 0 / 3)),
      ],
    }),
    booleanAttr({
      attributeId: "trigger.breach",
      label: "ARMI Trigger Level Breach",
      buckets: [
        lookupBucket(true, marker.fromColor("red"), "Yes"),
        lookupBucket(false, marker.fromColor("green"), "No"),
      ],
    }),
  ],
});

interface ChartOptions {
  showDistributionChart?: boolean;
  showTimelineChart?: boolean;
}

export const historyOptions: ChartOptions = {
  showDistributionChart: true,
  showTimelineChart: true,
};

export const statusOptions: ChartOptions = {
  showDistributionChart: true,
  showTimelineChart: false,
};

function countsGroup<A>(
  speciesEnum: Enum<A>,
  options: ChartOptions = {}
): AttributeGroup {
  const { showDistributionChart = true, showTimelineChart = true } = options;

  const numSpecies = speciesEnum.values.length;

  const speciesAttr = (value: A, label: string, index: number) =>
    numberAttr({
      attributeId: `count.${value}`,
      label,
      buckets: [
        rangeBucket(0, 1, white, "0"),
        rangeBucket(
          1,
          10,
          speciesColor((360 * index) / numSpecies, 3 / 3),
          "1-9"
        ),
        rangeBucket(
          10,
          100,
          speciesColor((360 * index) / numSpecies, 2 / 3),
          "10-99"
        ),
        rangeBucket(
          100,
          1000,
          speciesColor((360 * index) / numSpecies, 1 / 3),
          "100-999"
        ),
        rangeBucket(
          1000,
          null,
          speciesColor((360 * index) / numSpecies, 0 / 3),
          "1000+"
        ),
      ],
      showDistributionChart,
      showTimelineChart,
    });

  return attributeGroup({
    label: "Recorded Counts",
    attributes: speciesEnum.entries.map(({ value, label }, index) =>
      speciesAttr(value, label, index)
    ),
  });
}

function scoresGroup<A>(
  indexId: string,
  label: string,
  speciesEnum: Enum<A>,
  options: ChartOptions = {}
): AttributeGroup {
  const { showDistributionChart = true, showTimelineChart = true } = options;

  const numSpecies = speciesEnum.values.length;

  const totalAttr = numberAttr({
    attributeId: `total.${indexId}`,
    label: "Score",
    buckets: "auto",
  });

  const scoreAttr = (value: A, label: string, index: number) =>
    numberAttr({
      attributeId: `score.${indexId}.${value}`,
      label,
      buckets: [
        rangeBucket(null, 1, white, "0"),
        rangeBucket(1, 2, speciesColor((360 * index) / numSpecies, 3 / 3), "1"),
        rangeBucket(2, 3, speciesColor((360 * index) / numSpecies, 2 / 3), "2"),
        rangeBucket(3, 4, speciesColor((360 * index) / numSpecies, 1 / 3), "3"),
        rangeBucket(
          4,
          null,
          speciesColor((360 * index) / numSpecies, 0 / 3),
          "4"
        ),
      ],
      showDistributionChart,
      showTimelineChart,
    });

  const scoreAttrs = speciesEnum.entries.map(({ value, label }, index) =>
    scoreAttr(value, label, index)
  );

  return attributeGroup({
    label,
    attributes: [totalAttr, ...scoreAttrs],
  });
}

export function riverflyAttributeGroups(
  options: ChartOptions = {}
): AttributeGroup[] {
  return [
    siteGroup,
    triggerGroup,
    scoresGroup("armi", "Scores", RiverflySpeciesEnum, options),
    countsGroup(RiverflySpeciesEnum, options),
  ];
}

export function urbanRiverflyAttributeGroups(
  options: ChartOptions = {}
): AttributeGroup[] {
  return [
    siteGroup,
    triggerGroup,
    scoresGroup(
      "urban",
      "Urban Riverfly Scores",
      UrbanRiverflySpeciesEnum,
      options
    ),
    scoresGroup("armi", "ARMI Scores", RiverflySpeciesEnum, options),
    countsGroup(UrbanRiverflySpeciesEnum, options),
  ];
}

export function extendedRiverflyAttributeGroups(
  options: ChartOptions = {}
): AttributeGroup[] {
  return [
    siteGroup,
    triggerGroup,
    scoresGroup(
      "wq",
      "Water Quality Scores",
      ExtendedRiverflySpeciesEnum,
      options
    ),
    scoresGroup(
      "saf",
      "Silt and Flow Scores",
      ExtendedRiverflySpeciesEnum,
      options
    ),
    scoresGroup("armi", "ARMI Scores", RiverflySpeciesEnum, options),
    countsGroup(ExtendedRiverflySpeciesEnum, options),
  ];
}
